/**
 * ============================================================================
 * 主页内容 - Home Page Content
 * ============================================================================
 *
 * 【作用】Home 页所有文案/资源路径/可访问性文案的唯一入口（强一致命名空间：home.*）。
 * 【如何修改】改 home 下各字段的 en / cn。subtitle 支持 JSX（如 <strong>、<span className="...">）。
 * 图片放在 public/images/ 下，路径用 /images/... 。
 *
 * 【新架构说明】（第三人称客观注释）
 * - 全站通过 getText('home.xxx.yyy', lang) 获取 Home 的内容。
 * - 不再提供 hero.* 等历史别名，避免双路径维护成本。
 */

import React from 'react';

export const homeContent = {
  /**
   * ############################################################################
   * ############## HOME（命名空间根） #########################################
   * ############################################################################
   */
  home: {
    /**
     * ############################################################################
     * ############## 01. HERO CONTENT（主视觉文案） ##############################
     * ############################################################################
     */
        /**
     * ############################################################################
     * ############## Hero → 一句话定位（谁，在做什么） ##############################
     * ############################################################################
     */
    hero: {
      titlePrefix: { en: "Designing", cn: "设计与构筑" },
      titleSuffix: { en: "Chemical Quantum Systems", cn: "化学量子系统" },

      subtitle: {
        en: (
          <>
            We are building a cross-disciplinary platform of chemistry, physics, electrical engineering, computer science, and materials science to design and explore{" "}
            <span className="font-bold text-red-400">organic quantum materials</span>.
          </>
        ),
        cn: (
          <>
            我们正在构建涵盖化学，物理，电子工程，计算机科学和材料科学的跨学科平台，设计与探索<span className="font-bold text-red-400">有机量子材料</span>。
          </>
        )
      },

      btnResearch: { en: "Our Approach", cn: "了解我们的方法" },
      btnTeam: { en: "Our People", cn: "认识团队" },
      btnApplyNow: { en: "Join Us!", cn: "加入我们！" }
    },

    /**
     * ############################################################################
     * ############## 02. MISSION CONTENT（使命/愿景文案） ########################
     * ############################################################################
     */
    /**
     * ############################################################################
     * ############## Mission → 使命/愿景（为什么做） ##############################
     * ############################################################################
     */
    mission: {
      title: { en: "Our Mission", cn: "我们的使命" },
      body: {
        en: (
          <>
          <span className="font-bold text-red-400">Organic quantum materials</span> offer rich electronic states and quantum phenomena enabled by atomic-scale structural tunability. Our mission is to accelerate their discovery and establish a "<span className="font-bold text-pink-400">synthesis</span>-<span className="font-bold text-cyan-400">characterization</span>-<span className="font-bold text-purple-400">theory</span>-<span className="font-bold text-emerald-400">AI</span>" synergistic research framework to advance the understanding and control of quantum phenomena.
          </>
        ),
        cn: (
          <>
          <span className="font-bold text-red-400">有机量子材料</span>凭借分子尺度的可编辑结构与骨架调控能力，呈现出丰富的电子态与量子效应，为新型碳基量子材料与器件探索提供关键机遇。我们的使命是加速其发现，并构建“<span className="font-bold text-pink-400">合成</span>—<span className="font-bold text-cyan-400">表征</span>—<span className="font-bold text-purple-400">理论与计算</span>—<span className="font-bold text-emerald-400">AI</span>”协同研究体系，以推动量子现象的理解与调控。
          </>
        )
      }
    },

    /**
     * ############################################################################
     * ############## 03. ADVANTAGES CONTENT（优势文案） ##########################
     * ############################################################################
     */
    advantages: {
      loop: {
        title: { en: "Closed-Loop Innovation", cn: "闭环创新" },
        body: {
          en: `Our research is an iterative closed-loop framework composed of four interconnected pillars: synthesis, characterization, theory & computation, and AI acceleration. Each pillar feeds into the next, creating a dynamic feedback loop that drives scientific progress.`,
          cn: `我们的研究以四个部分构成的闭环迭代为核心，让材料设计、实验探索和理论分析在持续反馈中相互促进、逐步推进。`,
        }
      },
      fusion: {
        title: { en: "Interdisciplinary Fusion", cn: "跨学科融合" },
        body: {
          en: "Our platform brings together researchers across chemistry, physics, electrical engineering, computer science, and materials science, enabling collaborative exploration around shared scientific questions and rapid co-creation.",
          cn: "研究平台面向化学、物理、电子工程、计算机科学和材料科学等背景的协作，鼓励跨学科成员在同一问题框架下互相交流与合作，迸发创新火花。"
        }
      },
      learnMoreLabel: { en: "Learn More About Our Approach", cn: "深入了解我们的方法" }
    },

    /**
     * ############################################################################
     * ############## 04. PILLARS CONTENT（研究环节文案） #########################
     * ############################################################################
     */
    pillars: {
      title: { en: "Research Pillars", cn: "研究环节" },
      items: {
        synthesis: {
          title: { en: "Chemical Synthesis", cn: "化学合成" },
          desc: { en: "Organic precursor synthesis and on-surface reactions to create tailored quantum systems.", cn: "有机前驱体合成与表面反应构筑特殊设计的量子材料体系" }
        },
        characterization: {
          title: { en: "Condensed-Matter Characterization", cn: "凝聚态表征" },
          desc: { en: "Low-temperature scanning probe microscopy and complementary techniques to resolve structure and electronic properties.", cn: "低温扫描隧道显微镜 (STM/STS)为主，配合ARPES、光学光谱、磁性测量与输运等物理表征" }
        },
        theory: {
          title: { en: "Theory & Computation", cn: "理论与计算" },
          desc: { en: "Quantum chemistry/physics and first-principles calculations to guide design and interpret observations.", cn: "量子化学与物理第一性原理计算用于材料设计与结果解释" }
        },
        ai: {
          title: { en: "AI Acceleration", cn: "AI 加速" },
          desc: { en: "Data-driven models, ML force fields, and generative approaches to accelerate prediction and experiment planning.", cn: "数据驱动模型、机器学习力场与生成方法加速预测与实验设计" }
        }
      }
    },

    /**
     * ############################################################################
     * ############## 05. TEAM PREVIEW CONTENT（团队预览文案） ####################
     * ############################################################################
     */
    teamPreview: {
      viewAllLabel: { en: "View All Team Members", cn: "查看全部团队成员" }
    },

    /**
     * ############################################################################
     * ############## 06. EXPLORE MORE CONTENT（快速导航文案） ####################
     * ############################################################################
     */
    explore: {
      title: { en: "Explore More", cn: "探索更多" },
      subtitle: { en: "Dive deeper into our research, team, and achievements", cn: "深入了解我们的研究、团队和成果" },
      cards: {
        publications: {
          title: { en: "Publications", cn: "论文成果" },
          desc: { en: "View our latest research outcomes", cn: "查看我们的最新研究成果" },
          page: { en: "publications", cn: "publications" }
        },
        teaching: {
          title: { en: "Teaching", cn: "教学" },
          desc: { en: "Learn about our teaching philosophy and courses", cn: "了解我们的教学理念和课程" },
          page: { en: "teaching", cn: "teaching" }
        },
        news: {
          title: { en: "News", cn: "新闻动态" },
          desc: { en: "Get the latest lab updates", cn: "获取实验室最新动态" },
          page: { en: "news", cn: "news" }
        }
      }
    },

    /**
     * ############################################################################
     * ############## 07. ASSETS（图片/资源路径） ################################
     * ############################################################################
     */
    assets: {
      loopDiagramLight: { en: "/images/loop-diagram-light.png", cn: "/images/loop-diagram-light.png" },
      loopDiagramDark: { en: "/images/loop-diagram-dark.png", cn: "/images/loop-diagram-dark.png" },
      loopDiagramAlt: {
        en: "Closed-loop workflow diagram: synthesis–characterization–theory–AI",
        cn: "合成—表征—理论—AI 的闭环流程示意图"
      }
    },

    /**
     * ############################################################################
     * ############## 08. A11Y（可访问性 aria-label 文案），作为预案，未来可能需要修改 ########################
     * ############################################################################
     */
    a11y: {
      goResearch: { en: "Go to the research approach page", cn: "前往研究方法页面" },
      goTeam: { en: "Go to the team page", cn: "前往团队页面" },
      learnMoreApproach: { en: "Learn more about the research approach", cn: "了解更多研究方法" },
      viewAllTeam: { en: "View all team members", cn: "查看全部团队成员" },
      applyNow: { en: "Join Us!", cn: "加入我们！" }
    }
  }
};

/**
 * [维护提示｜术语全称]（第三人称客观注释）
 * 若页面希望对本科生/跨领域访问者更友好，可在首次出现处加入全称，例如：
 * - STM/STS: scanning tunneling microscopy/spectroscopy
 * - ARPES: angle-resolved photoemission spectroscopy
 * - MLFF: machine-learning force fields
 *
 * 该文件主要用于“Home 页的文案与资源引用”，不建议在此堆叠过多技术细节；
 * 细节更适合放在 Home.jsx 的 “闭环创新”段落或 Research 页面。
 */
